import { type ReactNode } from 'react';
import { useAuth } from './AuthProvider.js';
import type { UserRole } from '@aralere/shared-types';

interface RequireRoleProps {
  children: ReactNode;
  role: UserRole;
  /** What to show when the user lacks the role. */
  fallback?: ReactNode;
}

export function RequireRole({ children, role, fallback }: RequireRoleProps): JSX.Element {
  const { user, adminSurface } = useAuth();
  if (!user) return <>{fallback ?? null}</>;
  if (!user.roles.includes(role)) return <>{fallback ?? <NotAuthorized />}</>;
  // 08 Q10: even with the platform-admin role, admin routes render only where
  // the admin surface is permitted (ADMIN_HOSTNAME). Off-host, show NotAuthorized
  // instead of an admin page whose API calls would all 403 admin_host_only.
  // (Server-side withRole is the real gate; this keeps the UX coherent.)
  if (role === 'platform-admin' && !adminSurface) return <>{fallback ?? <NotAuthorized />}</>;
  return <>{children}</>;
}

function NotAuthorized(): JSX.Element {
  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="max-w-md text-center">
        <h1 className="font-display text-2xl mb-2">Not authorized</h1>
        <p className="text-sm text-[var(--color-muted)]">
          You don't have access to this area. If you believe this is a mistake, contact your administrator.
        </p>
      </div>
    </div>
  );
}
